import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { useUnread } from "@/context/UnreadContext";
import { useNotifications } from "@/context/NotificationsContext";
import { toast } from "sonner";
import {
  Bell, CheckCheck, Plane, ShoppingCart, FileText, CheckCircle2, ShieldAlert,
  Flag, Clock, Inbox, MessageSquare, Loader2, RefreshCw, AlertTriangle, Sunrise, Eye, Undo2,
} from "lucide-react";

const KIND_META = {
  leave_request: { icon: Plane, label: "Leave request" },
  leave_decision: { icon: Plane, label: "Leave decision" },
  purchase: { icon: ShoppingCart, label: "Purchase request" },
  reimbursement: { icon: ShoppingCart, label: "Reimbursement request" },
  purchase_decision: { icon: ShoppingCart, label: "Purchase decision" },
  reimbursement_decision: { icon: ShoppingCart, label: "Reimbursement decision" },
  weekly_report: { icon: FileText, label: "Weekly report" },
  weekly_report_decision: { icon: FileText, label: "Weekly report decision" },
  review_decision: { icon: CheckCircle2, label: "Review decision" },
  grievance: { icon: ShieldAlert, label: "Grievance" },
  grievance_decision: { icon: ShieldAlert, label: "Grievance decision" },
  milestone: { icon: Flag, label: "Milestone" },
  timesheet: { icon: Clock, label: "Timesheet" },
  client_request: { icon: Inbox, label: "Client request" },
  stale_project: { icon: AlertTriangle, label: "Project alert" },
  morning_digest: { icon: Sunrise, label: "Morning digest" },
  task_review: { icon: Eye, label: "Board · moved to review" },
  task_bounced: { icon: Undo2, label: "Board · bounced back" },
};

function metaFor(kind) {
  return KIND_META[kind] || { icon: Bell, label: (kind || "activity").replace(/_/g, " ") };
}

function statusTone(status) {
  const s = (status || "").toLowerCase();
  if (["approved", "done", "sent", "resolved", "hired", "active"].includes(s)) return "green";
  if (["rejected", "dismissed", "changes_requested"].includes(s)) return "red";
  if (["pending", "submitted", "new", "logged", "open"].includes(s)) return "orange";
  return "slate";
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Feb 2026 — Global Notification Center. "My Actions" = personal feed
 * (everyone); "Global feed" = every notification in the org, founder/admin
 * only. Chat unread is bridged in live from the existing UnreadContext
 * rather than duplicated into this collection. */
export default function Notifications() {
  const { user } = useAuth();
  const can = usePermissions();
  const { total: chatUnread } = useUnread();
  const { unread, refresh: refreshBadge, markRead: markReadShared, markAllRead: markAllReadShared } = useNotifications();
  const nav = useNavigate();
  const isAdmin = can("team_admin");
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { scope, unread_only: unreadOnly, limit: 200 } });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [scope, unreadOnly]);
  useEffect(() => { load(); refreshBadge(); }, [load, refreshBadge]);

  const openItem = async (n) => {
    if (scope === "mine" && !n.is_read) {
      await markReadShared(n.id);
      setItems((rows) => rows.map((r) => r.id === n.id ? { ...r, is_read: true } : r));
    }
    if (n.link) nav(n.link);
  };

  const markAllRead = async () => {
    await markAllReadShared();
    setItems((rows) => rows.map((r) => ({ ...r, is_read: true })));
    toast.success("All caught up");
  };

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto fade-in" data-testid="notifications-page">
      <PageHeader
        eyebrow="Activity"
        title="Notifications"
        subtitle="Approvals, decisions and updates across the studio — all in one place."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={load} data-testid="notifications-refresh">
              <RefreshCw size={14} className="mr-1.5" /> Refresh
            </Button>
            {scope === "mine" && unread > 0 && (
              <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={markAllRead} data-testid="notifications-mark-all-read">
                <CheckCheck size={14} className="mr-1.5" /> Mark all read
              </Button>
            )}
          </div>
        }
      />

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <button
          type="button" onClick={() => setScope("mine")}
          className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${scope === "mine" ? "bg-[#F54800] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          data-testid="notifications-scope-mine"
        >
          My Actions {unread > 0 && scope !== "mine" ? `(${unread})` : ""}
        </button>
        {isAdmin && (
          <button
            type="button" onClick={() => setScope("global")}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${scope === "global" ? "bg-[#F54800] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="notifications-scope-global"
          >
            Global feed
          </button>
        )}
        <label className="ml-2 inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="notifications-unread-only" />
          Unread only
        </label>
        {chatUnread > 0 && (
          <button
            type="button" onClick={() => nav("/chat")}
            className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#F54800] hover:underline"
            data-testid="notifications-chat-bridge"
          >
            <MessageSquare size={13} /> {chatUnread} unread chat message{chatUnread !== 1 ? "s" : ""}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-slate-500" data-testid="notifications-empty">
          <Bell size={22} className="mx-auto mb-2 text-slate-300" />
          Nothing here yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="notifications-list">
          {items.map((n) => {
            const { icon: Icon, label } = metaFor(n.kind);
            return (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left card-flat p-3.5 flex items-start gap-3 hover:border-slate-300 transition-colors ${n.is_read ? "opacity-70" : ""}`}
              >
                <div className="w-9 h-9 rounded-sm bg-orange-50 text-[#F54800] flex items-center justify-center shrink-0">
                  <Icon size={15} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
                    {n.status && <Tag tone={statusTone(n.status)}>{n.status}</Tag>}
                    {!n.is_read && scope === "mine" && <span className="w-1.5 h-1.5 rounded-full bg-[#F54800]" data-testid={`notification-unread-dot-${n.id}`} />}
                  </div>
                  <div className="text-sm font-medium text-slate-900 mt-0.5">{n.subject}</div>
                  {n.message && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>}
                  <div className="text-[11px] text-slate-400 mt-1">
                    {n.actor_name ? `${n.actor_name} · ` : ""}{timeAgo(n.created_at)}
                    {scope === "global" && <span> · to {n.user_id === user?.id ? "you" : (n.recipient_name || "team member")}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
